import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { api } from '../lib/api';
import { ApiError } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';
import { useAuth } from '../auth/AuthProvider';
import type {
  AdminActivationBatch,
  AdminCoupon,
  AdminOverview,
  AdminProduct,
  AdminStudent,
  AdminOrder,
  AdminBankTransaction,
  AdminCourseCurriculum,
  AdminSkillVersion,
  AdminWorkshop,
  AdminWorkshopRegistration,
  Cart,
  CheckoutResult,
  CouponValidation,
  HomepageSettings,
  HomepageSettingsInput,
  LibraryEntry,
  Order,
  ProductDetail,
  ProductSummary,
  ProductType,
  Profile,
  WorkshopDetail,
  WorkshopSummary,
  CreateActivationBatchInput,
  CreateAdminCouponInput,
  CreateAdminProductInput,
  CreatedActivationBatch,
  CreateAdminWorkshopInput,
} from '../lib/types';

/** How often a PENDING order is re-checked while waiting for the bank transfer. */
const ORDER_POLL_MS = 5000;

export function useHomepageSettings() {
  return useQuery({
    queryKey: queryKeys.homepage,
    queryFn: () => api.homepage(),
  }) as UseQueryResult<HomepageSettings, ApiError>;
}

export function useAdminHomepageSettings() {
  const { session } = useAuth();
  return useQuery({
    queryKey: queryKeys.adminHomepage,
    queryFn: () => api.adminHomepage(),
    enabled: Boolean(session),
  }) as UseQueryResult<HomepageSettings, ApiError>;
}

export function useUpdateHomepageSettings() {
  const queryClient = useQueryClient();
  return useMutation<HomepageSettings, ApiError, HomepageSettingsInput>({
    mutationFn: (input) => api.updateAdminHomepage(input),
    onSuccess: (settings) => {
      queryClient.setQueryData(queryKeys.homepage, settings);
      queryClient.setQueryData(queryKeys.adminHomepage, settings);
    },
  });
}

export function useUploadHomepageMedia() {
  return useMutation<
    { mediaUrl: string; mediaType: 'IMAGE' | 'VIDEO' },
    ApiError,
    File
  >({
    mutationFn: (file) => api.uploadHomepageMedia(file),
  });
}

export function useAdminOverview() {
  return useQuery({
    queryKey: queryKeys.adminOverview,
    queryFn: () => api.adminOverview(),
  }) as UseQueryResult<AdminOverview, ApiError>;
}

export function useAdminProducts(type?: ProductType) {
  return useQuery({
    queryKey: queryKeys.adminProducts(type),
    queryFn: () => api.adminProducts(type),
  }) as UseQueryResult<AdminProduct[], ApiError>;
}

export function useCreateAdminProduct() {
  const queryClient = useQueryClient();
  return useMutation<AdminProduct, ApiError, CreateAdminProductInput>({
    mutationFn: (input) => api.createAdminProduct(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminOverview });
      void queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useUpdateAdminProduct() {
  const queryClient = useQueryClient();
  return useMutation<AdminProduct, ApiError, { id: string; input: Partial<CreateAdminProductInput> }>({
    mutationFn: ({ id, input }) => api.updateAdminProduct(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
      void queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useUploadAdminProductThumbnail() {
  const queryClient = useQueryClient();
  return useMutation<{ thumbnailUrl: string }, ApiError, { id: string; file: File }>({
    mutationFn: ({ id, file }) => api.uploadAdminProductThumbnail(id, file),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.library });
    },
  });
}

export function useAdminStudents() {
  return useQuery({
    queryKey: queryKeys.adminStudents,
    queryFn: () => api.adminStudents(),
  }) as UseQueryResult<AdminStudent[], ApiError>;
}

export function useAdminCoupons() {
  return useQuery({
    queryKey: queryKeys.adminCoupons,
    queryFn: () => api.adminCoupons(),
  }) as UseQueryResult<AdminCoupon[], ApiError>;
}

export function useCreateAdminCoupon() {
  const queryClient = useQueryClient();
  return useMutation<AdminCoupon, ApiError, CreateAdminCouponInput>({
    mutationFn: (input) => api.createAdminCoupon(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCoupons });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminOverview });
    },
  });
}

export function useUpdateAdminCouponStatus() {
  const queryClient = useQueryClient();
  return useMutation<AdminCoupon, ApiError, { id: string; status: 'ACTIVE' | 'INACTIVE' }>({
    mutationFn: ({ id, status }) => api.updateAdminCouponStatus(id, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCoupons });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminOverview });
    },
  });
}

export function useAdminActivationBatches() {
  return useQuery({
    queryKey: queryKeys.adminActivationBatches,
    queryFn: () => api.adminActivationBatches(),
  }) as UseQueryResult<AdminActivationBatch[], ApiError>;
}

export function useCreateAdminActivationBatch() {
  const queryClient = useQueryClient();
  return useMutation<CreatedActivationBatch, ApiError, CreateActivationBatchInput>({
    mutationFn: (input) => api.createAdminActivationBatch(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminActivationBatches });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminOverview });
    },
  });
}

export function useAdminOrders() {
  return useQuery({ queryKey: queryKeys.adminOrders, queryFn: () => api.adminOrders() }) as UseQueryResult<AdminOrder[], ApiError>;
}

export function useAdminBankTransactions() {
  return useQuery({
    queryKey: queryKeys.adminBankTransactions,
    queryFn: () => api.adminBankTransactions(),
  }) as UseQueryResult<AdminBankTransaction[], ApiError>;
}

export function useReviewAdminBankTransaction() {
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, {
    id: string;
    input: { action: 'MATCH_ORDER' | 'IGNORE'; orderId?: string; note?: string };
  }>({
    mutationFn: ({ id, input }) => api.reviewAdminBankTransaction(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminOrders });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminBankTransactions });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminOverview });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminStudents });
    },
  });
}

export function useAdminCourseCurriculum(courseId?: string) {
  return useQuery({
    queryKey: queryKeys.adminCourseCurriculum(courseId ?? ''),
    queryFn: () => api.adminCourseCurriculum(courseId as string),
    enabled: Boolean(courseId),
  }) as UseQueryResult<AdminCourseCurriculum, ApiError>;
}

export function useCreateAdminCourseSection(courseId: string) {
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, { title: string; position: number }>({
    mutationFn: (input) => api.createAdminCourseSection(courseId, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.adminCourseCurriculum(courseId) }),
  });
}

export function useCreateAdminLesson(courseId: string, sectionId: string) {
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, { title: string; description?: string; position: number; isPreview?: boolean }>({
    mutationFn: (input) => api.createAdminLesson(sectionId, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.adminCourseCurriculum(courseId) }),
  });
}

export function useUpdateAdminLesson(courseId: string) {
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, { lessonId: string; input: { title?: string; description?: string; position?: number; isPreview?: boolean } }>({
    mutationFn: ({ lessonId, input }) => api.updateAdminLesson(lessonId, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.adminCourseCurriculum(courseId) }),
  });
}

export function useUploadLessonVideo(courseId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { lessonId: string; file: File }>({
    mutationFn: async ({ lessonId, file }) => {
      const maximumBasicUploadSize = 200 * 1024 * 1024;
      if (file.size > maximumBasicUploadSize) {
        throw new Error('Video lớn hơn 200 MB cần dùng chế độ tải lên có thể tiếp tục (tus).');
      }
      const upload = await api.createLessonVideoUpload(lessonId);
      await api.uploadVideoToStream(upload.uploadUrl, file);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.adminCourseCurriculum(courseId) }),
  });
}

export function useRefreshLessonVideoStatus(courseId: string) {
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, string>({
    mutationFn: (lessonId) => api.refreshLessonVideoStatus(lessonId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.adminCourseCurriculum(courseId) }),
  });
}

export function useAdminSkillVersions(skillId?: string) {
  return useQuery({
    queryKey: queryKeys.adminSkillVersions(skillId ?? ''),
    queryFn: () => api.adminSkillVersions(skillId as string),
    enabled: Boolean(skillId),
  }) as UseQueryResult<AdminSkillVersion[], ApiError>;
}

export function useUploadAdminSkillVersion(skillId: string) {
  const queryClient = useQueryClient();
  return useMutation<AdminSkillVersion, ApiError, { version: string; changelog: string; file: File }>({
    mutationFn: ({ version, changelog, file }) => api.uploadAdminSkillVersion(skillId, version, changelog, file),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminSkillVersions(skillId) });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
    },
  });
}

export function useAdminWorkshops() {
  return useQuery({ queryKey: queryKeys.adminWorkshops, queryFn: () => api.adminWorkshops() }) as UseQueryResult<AdminWorkshop[], ApiError>;
}

export function useCreateAdminWorkshop() {
  const queryClient = useQueryClient();
  return useMutation<AdminWorkshop, ApiError, CreateAdminWorkshopInput>({
    mutationFn: (input) => api.createAdminWorkshop(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminWorkshops });
      void queryClient.invalidateQueries({ queryKey: queryKeys.workshopsFeatured });
    },
  });
}

export function useUpdateAdminWorkshopStatus() {
  const queryClient = useQueryClient();
  return useMutation<AdminWorkshop, ApiError, { id: string; status: AdminWorkshop['status'] }>({
    mutationFn: ({ id, status }) => api.updateAdminWorkshopStatus(id, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminWorkshops });
      void queryClient.invalidateQueries({ queryKey: queryKeys.workshopsFeatured });
    },
  });
}

export function useAdminWorkshopRegistrations(workshopId?: string) {
  return useQuery({
    queryKey: queryKeys.adminWorkshopRegistrations(workshopId ?? ''),
    queryFn: () => api.adminWorkshopRegistrations(workshopId as string),
    enabled: Boolean(workshopId),
  }) as UseQueryResult<AdminWorkshopRegistration[], ApiError>;
}

export function useUpdateAdminWorkshopRegistrationStatus(workshopId: string) {
  const queryClient = useQueryClient();
  return useMutation<AdminWorkshopRegistration, ApiError, { id: string; status: AdminWorkshopRegistration['status'] }>({
    mutationFn: ({ id, status }) => api.updateAdminWorkshopRegistrationStatus(id, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.adminWorkshopRegistrations(workshopId) }),
  });
}

export function useProducts(filters: { type?: ProductType; featured?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.products(filters),
    queryFn: () => api.products(filters),
  }) as UseQueryResult<ProductSummary[], ApiError>;
}

export function useProduct(slug: string | undefined) {
  return useQuery({
    queryKey: queryKeys.product(slug ?? ''),
    queryFn: () => api.product(slug as string),
    enabled: Boolean(slug),
  }) as UseQueryResult<ProductDetail, ApiError>;
}

export function useFeaturedWorkshops() {
  return useQuery({
    queryKey: queryKeys.workshopsFeatured,
    queryFn: () => api.featuredWorkshops(),
  }) as UseQueryResult<WorkshopSummary[], ApiError>;
}

export function useWorkshop(slug: string | undefined) {
  return useQuery({
    queryKey: queryKeys.workshop(slug ?? ''),
    queryFn: () => api.workshop(slug as string),
    enabled: Boolean(slug),
  }) as UseQueryResult<WorkshopDetail, ApiError>;
}

export function useProfile() {
  const { session } = useAuth();
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: () => api.me(),
    enabled: Boolean(session),
  }) as UseQueryResult<Profile, ApiError>;
}

export function useLibrary() {
  const { session } = useAuth();
  return useQuery({
    queryKey: queryKeys.library,
    queryFn: () => api.library(),
    enabled: Boolean(session),
  }) as UseQueryResult<LibraryEntry[], ApiError>;
}

export function useCart() {
  const { session } = useAuth();
  return useQuery({
    queryKey: queryKeys.cart,
    queryFn: () => api.cart(),
    enabled: Boolean(session),
  }) as UseQueryResult<Cart, ApiError>;
}

/** Both cart mutations return the whole cart, so the cache is seeded directly. */
export function useAddToCart() {
  const queryClient = useQueryClient();
  return useMutation<Cart, ApiError, string>({
    mutationFn: (productId) => api.addToCart(productId),
    onSuccess: (cart) => queryClient.setQueryData(queryKeys.cart, cart),
  });
}

export function useRemoveFromCart() {
  const queryClient = useQueryClient();
  return useMutation<Cart, ApiError, string>({
    mutationFn: (productId) => api.removeFromCart(productId),
    onSuccess: (cart) => queryClient.setQueryData(queryKeys.cart, cart),
  });
}

export function useValidateCoupon() {
  return useMutation<CouponValidation, ApiError, string>({
    mutationFn: (code) => api.validateCoupon(code),
  });
}

export function useCheckout() {
  const queryClient = useQueryClient();
  return useMutation<CheckoutResult, ApiError, string | undefined>({
    mutationFn: (couponCode) => api.checkout(couponCode),
    onSuccess: (result) => {
      // Checkout closes the active cart server-side.
      void queryClient.invalidateQueries({ queryKey: queryKeys.cart });
      queryClient.setQueryData(queryKeys.order(result.order.id), result.order);
    },
  });
}

/**
 * Polls while the order is PENDING. Payment is confirmed by the SePay webhook,
 * so the order status is the only trustworthy signal.
 */
export function useOrder(orderId: string | undefined) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: queryKeys.order(orderId ?? ''),
    queryFn: async () => {
      const order = await api.order(orderId as string);
      if (order.status === 'PAID') {
        // Access grants land with the payment; refresh what depends on them.
        void queryClient.invalidateQueries({ queryKey: queryKeys.library });
      }
      return order;
    },
    enabled: Boolean(orderId),
    refetchInterval: (query) =>
      query.state.data?.status === 'PENDING' ? ORDER_POLL_MS : false,
  }) as UseQueryResult<Order, ApiError>;
}

export function useRedeemActivationCode() {
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, string>({
    mutationFn: (code) => api.redeemActivationCode(code),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.library }),
  });
}
